// Edit + review + post a DRAFT goods receipt (BUILD_SPEC.md §7, §8.1 steps 3-5),
// or VIEW a POSTED/REVERSED one read-only (ReceiptsRegister's "View" action,
// G-WebA). Used for a lingering DRAFT found in the register (a wizard run that
// stopped short of Post), the clone created by "Reverse & clone" on a POSTED
// goods receipt (plan.md G20, QC fix #10), and also ReceiveLoadWizard's
// ticket-less (no-ticket) branch — a docket receipt with no weighbridge ticket
// (Stuart ruling 2026-08-25 folded that flow into "Receive a load" as an
// optional step; it opens this same modal by grId rather than duplicating its
// UI). Shows the same ledger-effect preview math as ReceiveLoadWizard's review
// step, one line at a time — mode-aware (mass vs EACH/count, G-WebA) — so a
// multi-line receipt is just as transparent as the common single-line case.
import { useEffect, useState } from 'react';
import { Alert, Badge, Button, Group, Modal, Paper, Select, Stack, Table, Text, TextInput, Tooltip } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { ApiRequestError } from '../../api/client';
import { useCanOperate, useIsAdmin } from '../../auth/permissions';
import { isCountLine, isMassLine, type GoodsReceiptLineInput } from '@zebra/core';
import { formatMoneyCents, formatQtyDisplay, nowForDatetimeLocal, PRICING_BASIS_LABELS, toDatetimeLocalValue } from './common';
import { computeEachLedgerPreview, computeLedgerPreview } from './ledgerPreview';
import { useGoodsReceipt, useLocations, usePostGoodsReceipt, usePurchaseOrder, useProducts, useUpdateGoodsReceipt } from './api';

interface LineInputState {
  qtyKg: string;
  mcPct: string;
  qty: string;
}

export function GoodsReceiptDraftModal({ grId, onClose }: { grId: string | null; onClose: () => void }) {
  const canOperate = useCanOperate();
  const isAdmin = useIsAdmin();
  const { data: gr } = useGoodsReceipt(grId ?? undefined);
  const { data: po } = usePurchaseOrder(gr?.poId);
  const { data: products } = useProducts();
  const { data: locations } = useLocations({ siteId: po?.siteId, isActive: true });
  const update = useUpdateGoodsReceipt(grId ?? '');
  const post = usePostGoodsReceipt(grId ?? '');

  const [locationId, setLocationId] = useState('');
  const [receivedAt, setReceivedAt] = useState(nowForDatetimeLocal());
  const [lineInputs, setLineInputs] = useState<Record<string, LineInputState>>({});
  // Out-of-band ADMIN override reasons (spec §7 amendment R8), keyed by GR line
  // id — surfaced only once a real Post attempt has actually 422'd
  // MOISTURE_OUT_OF_BAND (see `postFailedOutOfBand` below), not pre-emptively.
  const [overrideReasons, setOverrideReasons] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!gr) return;
    setLocationId(gr.locationId);
    setReceivedAt(toDatetimeLocalValue(gr.receivedAt));
    // A count (EACH) line has qtyKg/mcPct null and a mass line has qty null —
    // coalesce both for the controlled inputs below (isCountLine/isMassLine
    // picks which one is actually rendered per line).
    setLineInputs(Object.fromEntries(gr.lines.map((l) => [l.id, { qtyKg: l.qtyKg ?? '', mcPct: l.mcPct ?? '', qty: l.qty ?? '' }])));
  }, [gr]);

  if (!grId || !gr || !po) return null;

  const readOnly = gr.status !== 'DRAFT';
  const productName = new Map((products?.items ?? []).map((p) => [p.id, `${p.sku} — ${p.name}`]));
  const poLineById = new Map(po.lines.map((l) => [l.id, l]));

  if (readOnly) {
    return (
      <Modal opened onClose={onClose} title={`Goods receipt ${gr.number} (${gr.status})`} centered size="lg">
        <Stack gap="md">
          <Table.ScrollContainer minWidth={640}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Product</Table.Th>
                  <Table.Th>Qty</Table.Th>
                  <Table.Th>MC %</Table.Th>
                  <Table.Th>Payable</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {gr.lines.map((line) => {
                  const each = isCountLine(line);
                  return (
                    <Table.Tr key={line.id}>
                      <Table.Td>{productName.get(line.productId) ?? line.productId}</Table.Td>
                      <Table.Td>{each ? `${formatQtyDisplay(line.qty, 0)} units` : `${formatQtyDisplay(line.qtyKg)} kg`}</Table.Td>
                      <Table.Td>{each ? '—' : line.mcPct ?? '—'}</Table.Td>
                      <Table.Td>{line.payableCents != null ? formatMoneyCents(line.payableCents) : '—'}</Table.Td>
                      <Table.Td>
                        {line.mcOverridden && (
                          <Tooltip label={line.mcOverrideReason ?? 'Out-of-band moisture reading, ADMIN override'} multiline w={260}>
                            <Badge color="amber">Overridden</Badge>
                          </Tooltip>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          <Group justify="flex-end">
            <Button onClick={onClose}>Close</Button>
          </Group>
        </Stack>
      </Modal>
    );
  }

  const previews = gr.lines.map((line) => {
    const input = lineInputs[line.id] ?? { qtyKg: line.qtyKg ?? '', mcPct: line.mcPct ?? '', qty: line.qty ?? '' };
    const poLine = poLineById.get(line.poLineId);
    if (!poLine) return { line, poLine: undefined, input, preview: { ok: false as const, error: 'PO line not found', code: undefined } };
    if (isCountLine(line)) {
      return { line, poLine, input, preview: computeEachLedgerPreview({ qty: input.qty, priceCentsPerUnit: poLine.priceCentsPerUnit }) };
    }
    return {
      line,
      poLine,
      input,
      preview: computeLedgerPreview({
        basis: poLine.pricingBasis ?? 'FLAT_WET',
        netKg: input.qtyKg,
        mcPct: input.mcPct || null,
        priceCentsPerTonne: poLine.priceCentsPerTonne,
        bands: poLine.bands,
      }),
    };
  });

  // A line hard-blocks Post for any reason OTHER than an out-of-band moisture
  // reading (missing reading, missing contract price, mismatched PO line...).
  // Out-of-band alone does NOT hard-block — Post stays clickable, the real
  // POST 422s MOISTURE_OUT_OF_BAND, and an ADMIN resolves it via the override
  // control below instead of being locked out before ever attempting it.
  const outOfBandLineIds = previews.filter((p) => !p.preview.ok && p.preview.code === 'MOISTURE_OUT_OF_BAND').map((p) => p.line.id);
  const hardBlocked = previews.some((p) => !p.preview.ok && p.preview.code !== 'MOISTURE_OUT_OF_BAND');
  const allOk = previews.every((p) => p.preview.ok);
  const totalPayable = previews.reduce((sum, p) => (p.preview.ok ? sum + p.preview.result.payableCents : sum), 0n);

  /** Mode-aware line bodies (mirrors ReceiptsRegister's clone-body gate fix): a
   * count (EACH) line round-trips as `{qty}`, never a mass shape with a null
   * `qtyKg` the server would 400. `withOverrides` attaches `mcOverrideReason`
   * to exactly the lines the client has independently identified as
   * out-of-band (never to an in-band line — see intake.ts's own doc on why
   * that would just be ignored, but there's no reason to send it either). */
  function buildLinesBody(withOverrides: boolean): GoodsReceiptLineInput[] {
    return gr!.lines.map((l) => {
      if (isMassLine(l)) {
        const reason = withOverrides && outOfBandLineIds.includes(l.id) ? overrideReasons[l.id]?.trim() || undefined : undefined;
        return {
          poLineId: l.poLineId,
          productId: l.productId,
          qtyKg: lineInputs[l.id]?.qtyKg ?? l.qtyKg!,
          mcPct: lineInputs[l.id]?.mcPct || null,
          ...(reason ? { mcOverrideReason: reason } : {}),
        };
      }
      return { poLineId: l.poLineId, productId: l.productId, qty: lineInputs[l.id]?.qty ?? l.qty! };
    });
  }

  function handleSaveThenPost(withOverrides: boolean) {
    if (!gr || !po) return;
    update.mutate(
      {
        poId: gr.poId,
        ticketId: gr.ticketId ?? undefined,
        locationId,
        receivedAt: new Date(receivedAt),
        notes: gr.notes,
        lines: buildLinesBody(withOverrides),
      },
      { onSuccess: () => post.mutate(undefined, { onSuccess: onClose }) },
    );
  }

  const postFailedOutOfBand = post.isError && post.error instanceof ApiRequestError && post.error.code === 'MOISTURE_OUT_OF_BAND';
  const overridesReady = outOfBandLineIds.length > 0 && outOfBandLineIds.every((id) => (overrideReasons[id]?.trim().length ?? 0) >= 3);

  return (
    <Modal opened onClose={onClose} title={`Goods receipt ${gr.number} (DRAFT)`} centered size="lg">
      <Stack gap="md">
        {(update.isError || post.isError) && !postFailedOutOfBand && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {update.error instanceof ApiRequestError
              ? update.error.message
              : post.error instanceof ApiRequestError
                ? post.error.message
                : 'Failed to post the goods receipt'}
          </Alert>
        )}

        <Group grow>
          <Select
            label="Unload location"
            data={(locations?.items ?? []).map((l) => ({ value: l.id, label: `${l.code} — ${l.name}` }))}
            value={locationId}
            onChange={(v) => setLocationId(v ?? '')}
          />
          <TextInput label="Received at" type="datetime-local" value={receivedAt} onChange={(e) => setReceivedAt(e.currentTarget.value)} />
        </Group>

        <Table.ScrollContainer minWidth={640}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Product</Table.Th>
                <Table.Th>Qty</Table.Th>
                <Table.Th>MC %</Table.Th>
                <Table.Th>Payable</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {previews.map(({ line, poLine, input, preview }) => {
                const each = isCountLine(line);
                return (
                  <Table.Tr key={line.id}>
                    <Table.Td>
                      <Text size="sm">{productName.get(line.productId) ?? line.productId}</Text>
                      <Text size="xs" c="dimmed">
                        {poLine ? (each ? 'EACH (count)' : PRICING_BASIS_LABELS[poLine.pricingBasis ?? 'FLAT_WET']) : ''}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {each ? (
                        <TextInput
                          size="xs"
                          value={input.qty}
                          onChange={(e) => setLineInputs((prev) => ({ ...prev, [line.id]: { ...prev[line.id]!, qty: e.currentTarget.value } }))}
                          w={110}
                        />
                      ) : (
                        <TextInput
                          size="xs"
                          value={input.qtyKg}
                          onChange={(e) => setLineInputs((prev) => ({ ...prev, [line.id]: { ...prev[line.id]!, qtyKg: e.currentTarget.value } }))}
                          w={110}
                        />
                      )}
                    </Table.Td>
                    <Table.Td>
                      {each ? (
                        <Text size="xs" c="dimmed">
                          —
                        </Text>
                      ) : (
                        <TextInput
                          size="xs"
                          value={input.mcPct}
                          onChange={(e) => setLineInputs((prev) => ({ ...prev, [line.id]: { ...prev[line.id]!, mcPct: e.currentTarget.value } }))}
                          w={90}
                        />
                      )}
                    </Table.Td>
                    <Table.Td>
                      {preview.ok ? (
                        formatMoneyCents(preview.result.payableCents)
                      ) : (
                        <Text c={preview.code === 'MOISTURE_OUT_OF_BAND' ? 'amber' : 'red'} size="xs">
                          {preview.error}
                        </Text>
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>

        <Paper withBorder p="sm" radius="md" bg="var(--mantine-color-green-light)">
          <Text size="sm">
            Ledger effect: {gr.lines.length} line(s) received, total payable{' '}
            <strong>{allOk ? formatMoneyCents(totalPayable) : '—'}</strong>
            {!allOk && ' (resolve the line(s) above first)'}.
          </Text>
        </Paper>

        {postFailedOutOfBand && (
          <Alert color={isAdmin ? 'amber' : 'red'} icon={<IconAlertCircle size={16} />} title="Out-of-band moisture reading">
            <Stack gap="xs">
              <Text size="sm">
                {post.error instanceof ApiRequestError ? post.error.message : 'A line’s moisture reading is outside every configured band.'}
              </Text>
              {isAdmin ? (
                <>
                  {outOfBandLineIds.map((lineId) => {
                    const line = gr.lines.find((l) => l.id === lineId);
                    return (
                      <TextInput
                        key={lineId}
                        label={`Override reason — ${line ? productName.get(line.productId) ?? line.productId : lineId}`}
                        placeholder="Override out-of-band moisture — reason (required, min 3 chars)"
                        value={overrideReasons[lineId] ?? ''}
                        onChange={(e) => setOverrideReasons((prev) => ({ ...prev, [lineId]: e.currentTarget.value }))}
                      />
                    );
                  })}
                  <Group justify="flex-end">
                    <Button
                      color="amber"
                      disabled={!overridesReady}
                      loading={update.isPending || post.isPending}
                      onClick={() => handleSaveThenPost(true)}
                    >
                      Post with override
                    </Button>
                  </Group>
                </>
              ) : (
                <Text size="sm" c="dimmed">
                  Ask an ADMIN to override this reading, or correct it and try again.
                </Text>
              )}
            </Stack>
          </Alert>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Close
          </Button>
          <Tooltip label="Requires Operations role" disabled={canOperate}>
            <span>
              <Button
                disabled={!canOperate || hardBlocked || !locationId}
                loading={update.isPending || post.isPending}
                onClick={() => handleSaveThenPost(false)}
              >
                Post
              </Button>
            </span>
          </Tooltip>
        </Group>
      </Stack>
    </Modal>
  );
}
